import React from 'react';

import Seo from '../ui/Seo';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import ServiceList from '../sections/ServiceList';
import ProcessRail from '../sections/ProcessRail';
import Pledge from '../sections/Pledge';
import SectionHead from '../sections/SectionHead';
import Faqs from '../sections/Faqs';
import Cta from '../sections/Cta';
import { site } from '../data/site';

import HeroImg from '../media/images/residential-services/electrical-panel-upgrades.jpg';

const servicesContent = [
    {
        category: "Residential Services",
        items: [
            {
                title: "Electrical Panel Upgrades & Replacements",
                text: "As your home evolves, your electrical system must adapt to meet increasing demands. Upgrading panels ensures safety and prevents overloads, especially if your panel is 25 years or older.",
            },
            {
                title: "New Construction & Renovations",
                text: "Whether you're building a new home or renovating an existing one, we provide durable and energy-efficient electrical solutions that meet all safety codes.",
            },
            {
                title: "LED Recessed Lighting",
                text: "Modernize your home with LED recessed lighting. These fixtures offer energy savings, reduced heat, and a lifespan 10x longer than traditional bulbs.",
            },
            {
                title: "Service Calls",
                text: "From minor issues to emergencies, our electricians are ready to resolve electrical problems quickly and safely, ensuring lasting results.",
            },
            {
                title: "Generator Installations",
                text: "We handle the complete installation process for your generator, including site selection, installation, fuel connection, and final testing.",
            },
            {
                title: "Service Upgrades",
                text: "Upgrade outdated systems with efficient electrical solutions tailored to meet the demands of modern living.",
            },
            {
                title: "Landscape Lighting",
                text: "Enhance your outdoor spaces with customized lighting solutions that balance ambiance, safety, and functionality.",
            },
            {
                title: "General Repairs & Installations",
                text: "From installations to troubleshooting, we provide same-day solutions for a wide range of electrical needs.",
            },
            {
                title: "Smart Homes",
                text: "Integrate smart technology into your home with automated lighting, security, media, and more, all controlled remotely for added convenience.",
            },
            {
                title: "A/C Hookups",
                text: "Ensure your cooling and heating systems run efficiently with our professional and precise A/C hookup services.",
            },
        ],
    },
    {
        category: "Commercial Services",
        items: [
            {
                title: "Diagnosis & Repairs",
                text: "We specialize in troubleshooting and repairing electrical issues to minimize downtime and maintain business operations.",
            },
            {
                title: "Installations & Upgrades",
                text: "Upgrade your commercial space with reliable electrical installations tailored to your business needs.",
            },
            {
                title: "Specialty Lighting",
                text: "Create custom lighting solutions to enhance aesthetics and functionality for storefronts, offices, and more.",
            },
            {
                title: "New Construction",
                text: "Our licensed electricians provide expert services for new commercial builds, ensuring compliance and efficiency from the ground up.",
            },
            {
                title: "Parking Lot Lighting",
                text: "Improve safety and visibility with professional installation and maintenance of parking lot and pathway lighting.",
            },
            {
                title: "Landscape & Security Wiring",
                text: "Secure your property with advanced wiring for security systems, motion sensors, and lighting.",
            },
            {
                title: "Warehouse Lighting",
                text: "Optimize lighting in your warehouse to boost productivity and safety while adhering to code standards.",
            },
            {
                title: "Office Renovations",
                text: "Revamp your office with energy-efficient electrical upgrades, ensuring a modern and productive work environment.",
            },
            {
                title: "Code Upgrades & Code Corrections",
                text: "Stay compliant with up-to-date code standards. Our experts identify and resolve code violations effectively.",
            },
            {
                title: "Exit & Emergency Lighting",
                text: "Ensure safety and legal compliance with professionally installed exit and emergency lighting solutions.",
            },
            {
                title: "Surge Protection",
                text: "Protect your equipment and electrical systems from power surges with durable surge protection solutions.",
            },
            {
                title: "EV Charging Stations",
                text: "Adapt to the future with electric vehicle charging stations that support sustainability and attract modern customers.",
            },
        ],
    },
];

export default function Services() {
    return (
        <>
            <Seo
                title="Our Services"
                description="Discover the range of services offered by Smart Electric Solutions, including residential and commercial electrical solutions tailored to your needs."
                keywords="Smart Electric Solutions, residential services, commercial services, electrical upgrades, lighting installations, surge protection, generator installations"
                path="/services"
                schema={{
                    '@type': 'Service',
                    serviceType: 'Electrical Services',
                    provider: {
                        '@type': 'Organization',
                        name: site.name,
                        url: site.origin
                    },
                    areaServed: 'South Florida',
                    hasOfferCatalog: {
                        '@type': 'OfferCatalog',
                        name: 'Electrical services',
                        itemListElement: servicesContent.flatMap(category =>
                            category.items.map(service => ({
                                '@type': 'Offer',
                                itemOffered: { '@type': 'Service', name: service.title }
                            }))
                        )
                    }
                }}
            />

            <PageHero
                eyebrow="Services"
                title="Everything from the meter to the last cover plate."
                lead="Comprehensive electrical services for homes and businesses across South Florida, performed by licensed and certified professionals."
                backdrop={HeroImg}
                focus="20% center"
                crumbs={[{ label: 'Services' }]}
            />

            <TradeTicker />

            {servicesContent.map((category, index) => (
                <ServiceList
                    key={category.category}
                    id={index === 0 ? 'service-list' : undefined}
                    eyebrow={index === 0 ? 'The full list' : 'And also'}
                    title={category.category}
                    items={category.items}
                />
            ))}

            <ProcessRail />

            <section className="section section--raised">
                <div className="shell">
                    <SectionHead
                        eyebrow="The commitment"
                        title="What you get, every time."
                        center
                    />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <Pledge />
                    </div>
                </div>
            </section>

            <Faqs />
            <Cta />
        </>
    );
}
